// The **GolfHolidayDirect** component showcases golf holiday offerings, including a hero banner, partnership details,
// top resorts, and a signup section. It features a responsive carousel for displaying top resorts,
// allowing users to navigate through multiple slides. The component dynamically loads content from the provided data
// source and ensures responsiveness using **useMediaQuery** for various screen sizes. Styled for a seamless user experience,
//  it includes interactive elements such as **navigation buttons** and a **signup form** to engage users effectively.
// paragraph - typography

import React, { useMemo, useState } from 'react';
import { GolfHolidayDataSource } from 'types/next';
import Link from 'components/commercetools-ui/atoms/link';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { desktop, mediumDesktop, tablet, mobile } from 'helpers/utils/screensizes';
import { ChevronRightIcon, ChevronLeftIcon } from '@heroicons/react/24/solid';
import Input, { InputProps } from 'components/commercetools-ui/atoms/input';
import { useAccount } from 'frontastic';
import useValidate from 'helpers/hooks/useValidate';
import { useFormat } from 'helpers/hooks/useFormat';
import Message from 'components/commercetools-ui/atoms/message';
import useLocale from 'helpers/hooks/useLocale';

interface GolfHolidayDirectProps {
  golfHoliday: GolfHolidayDataSource;
}

const GolfHolidayDirect = ({ golfHoliday }: GolfHolidayDirectProps) => {
  const heroContent = golfHoliday?.heroBanner || '';
  const patnershipInfo = golfHoliday?.partnershipInfo || '';
  const topResorts = golfHoliday?.topResorts || '';
  const signUp = golfHoliday?.signUp || '';
  const contentItems = topResorts?.content || [];
  const totalItems = contentItems.length;
  const [currentIndex, setCurrentIndex] = useState(0);
  // Responsive slides per view
  const isMediumDesktop = useMediaQuery(mediumDesktop);
  const isTablet = useMediaQuery(tablet);
  const isDesktop = useMediaQuery(desktop);
  const isMobile = useMediaQuery(mobile);
  const [message, setMessage] = useState({ errorMessage: '', severity: '' });
  const [isLoading, setIsLoading] = useState(false);
  const { newsletterSignup } = useAccount();
  const [emailData, setEmailData] = useState('');
  const { formatMessage } = useFormat();
  const { validateEmail } = useValidate();

  const slidesPerView = useMemo(() => {
    const isMobileView = isMobile?.[0] ?? false;
    const isTabletView = isTablet?.[0] ?? false;
    const isDesktopView = isDesktop?.[0] ?? false;
    const isMediumDesktopView = isMediumDesktop?.[0] ?? false;

    if (isMobileView) return 4; // Mobile: 2 slides
    if (isTabletView) return 4; // Tablet: 4 slides
    if (isMediumDesktopView) return 4; // Medium Desktop: 4 slides
    if (isDesktopView) return 4; // Desktop: 4 slides
    return 2;
  }, [isDesktop, isTablet, isMediumDesktop, isMobile]);

  const handlePrev = () => {
    setCurrentIndex((prevIndex) => (prevIndex - slidesPerView + totalItems) % totalItems);
  };

  const handleNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex + slidesPerView) % totalItems);
  };

  const [isExpanded, setIsExpanded] = useState(false);
  const toggleReadMore = () => setIsExpanded(!isExpanded);
  const { locale } = useLocale();
  let newsletterLocale = 'americangolf-GB';

  if (locale === 'en_EU') {
    newsletterLocale = 'americangolf-EU';
  }

  const handleNewspaperSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (emailData.length === 0 || !validateEmail(emailData)) {
      setMessage({
        severity: 'error',
        errorMessage:
          'An error occured so we were unable to subscribe you to our newsletter. Please attend the hint below.',
      });
      return;
    }
    try {
      setIsLoading(true);
      const res = await newsletterSignup(emailData, newsletterLocale);

      if (res.statusCode !== 200) {
        setMessage({
          severity: 'error',
          errorMessage: 'Something went wrong while trying to subscribe to our newsletter.',
        });
        return;
      }
      setMessage({
        severity: 'success',
        errorMessage: 'You are successfully subscribed to our newsletter',
      });
    } catch (error) {
      console.error('Failed to signup to newsletter', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmailData(e.target.value);
  };

  const emailField: InputProps = {
    label: formatMessage({ id: 'email', defaultMessage: 'Email' }),
    name: 'email',
    errorMessage: 'This field is required.',
    validation(value) {
      return validateEmail(value);
    },
    required: true,
  };

  return (
    <div>
      <Link link={heroContent?.link}>
        <div className="hero-section relative mb-20">
          <img
            src={`https://americangolf.a.bigcontent.io/v1/static/${heroContent?.heroImage?.name}`}
            className="hidden w-[100%] md:block"
          />
          <img
            src={`https://americangolf.a.bigcontent.io/v1/static/${heroContent?.heroMobileImage?.name}`}
            className="h-[340px] w-[100%] md:hidden"
          />

          <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center">
            <h2 className="font-body text-5xl font-bold text-white md:text-6xl">GOLF HOLIDAYS AND BREAKS</h2>

            <div className="mx-auto max-w-[81%]">
              <p className="mt-15 p-0 text-lg font-semibold text-white md:text-xl">
                BOOK WITH CONFIDENCE KNOWING YOUR GOLF HOLIDAY IS ATOL PROTECTED
              </p>
            </div>
          </div>
        </div>
      </Link>

      <div className="patnershipInfo-section mb-20 lg:mx-auto lg:w-[1000px] 2xl:w-[73rem]">
        <div className="mx-auto text-center">
          <h2 className="font-body text-[16px] font-semibold uppercase leading-[28px] text-[#e52225]">
            {patnershipInfo?.partnershipInfo}
          </h2>
        </div>

        <Link link={patnershipInfo?.link} className="flex justify-center">
          <img
            src={`https://americangolf.a.bigcontent.io/v1/static/${patnershipInfo?.image?.name}`}
            className="w-[100%] md:max-w-[500px]"
          />
        </Link>

        <div className="mx-auto mb-[40px] mt-[30px] flex max-w-[1000px] flex-col items-center justify-center p-15">
          {/* Full text for larger screens, truncated text for mobile */}
          <p className="text-center font-body text-18 font-normal leading-8">
            <span className="block font-normal sm:hidden">
              {isExpanded
                ? patnershipInfo?.description
                : `${patnershipInfo?.description.split(' ').slice(0, 53).join(' ')}...`}
            </span>
            <span className="hidden font-normal sm:block">{patnershipInfo?.description}</span>
          </p>

          {/* Read More / Read Less button - visible only on small screens */}
          {patnershipInfo?.description?.split(' ').length > 52 && (
            <button onClick={toggleReadMore} className="relative mt-4 flex items-center px-6 py-2 text-black sm:hidden">
              <span className="text-base font-semibold">{isExpanded ? 'Read Less' : 'Read More'}</span>
              <span className="ml-3 text-2xl font-semibold" style={{ color: 'rgb(18, 126, 87)' }}>
                {isExpanded ? '-' : '+'}
              </span>
            </button>
          )}
        </div>
      </div>

      <div className="Top-Resorts-section bg-[#f5f4f3] py-50 md:px-50">
        <div className="flex items-center justify-center">
          <h1 className="mb-30 font-body text-36 font-bold leading-9">{topResorts?.overallTitle}</h1>
        </div>
        <div className="relative z-10 mt-20">
          {/* Navigation Buttons */}
          <div className="absolute left-0 right-0 top-1/2 flex -translate-y-1/2 justify-between px-4">
            <button
              className="border-box absolute left-[2%] top-[-15px] z-[1] flex h-[50px] w-[30px] cursor-pointer 
             items-center justify-center rounded-[4px] bg-[rgb(30,30,30)] text-center 
             font-[400] text-[rgb(255,255,255)] shadow-md outline-none 
             transition-[color,background-color,border-color,opacity] 
             duration-[0.25s] ease-in-out hover:bg-[#151461]"
              onClick={handlePrev}
            >
              <ChevronLeftIcon
                className="mt-2 h-20 w-20 font-bold text-[#fff]"
                stroke="currentColor"
                strokeWidth="3"
                fill="none"
              />
            </button>

            <button
              className="border-box absolute right-[2%] top-[-15px] z-[1] flex h-[50px] w-[30px] cursor-pointer 
             items-center justify-center rounded-[4px] bg-[rgb(30,30,30)] text-center 
             font-[400] text-[rgb(255,255,255)] shadow-md outline-none 
             transition-[color,background-color,border-color,opacity] 
             duration-[0.25s] ease-in-out hover:bg-[#151461]"
              onClick={handleNext}
            >
              <ChevronRightIcon
                className="mt-2 h-20 w-20 font-bold text-[#fff]"
                stroke="currentColor"
                strokeWidth="3"
                fill="none"
              />
            </button>
          </div>

          {/* Carousel Items */}
          <div className="mx-[5%] flex justify-center gap-x-2 sm:gap-x-3 md:gap-6 md:overflow-visible md:px-5 lg:gap-6 xl:gap-5 2xl:gap-5">
            {contentItems.length > 0 &&
              [...contentItems, ...contentItems]
                .slice(currentIndex, currentIndex + slidesPerView)
                .map((item: any, index: number) => (
                  <div key={index} className="group flex flex-col items-center">
                    {/* Image Container */}
                    <Link
                      link={item.link}
                      className="my-[10px] block text-center transition-all duration-300 ease-in-out group-hover:opacity-40"
                    >
                      <div className="flex w-full items-center justify-center">
                        <div className="flex h-[63px] w-[126px] items-center justify-center md:h-[77px] md:w-[154px] lg:h-[105px] lg:w-[211px]">
                          <img
                            src={`https://americangolf.a.bigcontent.io/v1/static/${item.image.name}`}
                            alt={item.title}
                            className="h-full w-full object-contain"
                          />
                        </div>
                      </div>

                      {/* Title & Description */}
                      <div className="mt-2 w-full text-center">
                        <h3 className="my-[10px] px-[36px] text-[17px] font-semibold uppercase tracking-wide text-black md:text-[18px] xl:text-[22px]">
                          {item.title}
                        </h3>

                        <p className="text-[15px] text-black md:text-base">
                          <i>{item.description}</i>
                        </p>
                      </div>
                    </Link>
                  </div>
                ))}
          </div>
        </div>

        <div className="mt-10 flex items-center justify-center">
          <Link link={topResorts?.cta?.link}>
            {/* <button className="h-[58px] w-full bg-[#1b1b1b] font-body text-16 uppercase leading-4 text-white hover:bg-[#e40036] md:w-[322px]">
              {topResorts?.cta?.text}
            </button> */}
            <button
              className="border-box relative block h-[58px] w-[240px] rounded-[5px] border-b-[0.666667px] border-l-[0.666667px] border-r-[0.666667px] border-t-[0.666667px] border-solid border-[#000] bg-[#1b1b1b] px-[5px] 
                   pb-[18px] pt-[18px] font-body text-[18px] font-semibold uppercase 
                   leading-4 text-white transition-all duration-300 ease-in-out 
                   hover:bg-[#e40036] sm:w-[325px] md:w-[325px]"
            >
              {topResorts?.cta?.text}
            </button>
          </Link>
        </div>
      </div>
      <div className="signup-section my-25 flex flex-col items-center justify-center space-y-10 lg:mx-auto lg:w-[1000px] 2xl:w-[73rem]">
        <h1 className="text-center font-bold md:text-[29px] lg:text-36">{signUp?.title}</h1>
        <p className="ml-35 mr-35 text-center text-18 font-normal leading-6 text-gray-700">{signUp?.description}</p>

        <div className="w-full px-[40px] md:mx-[202.635px] md:w-[547px] md:px-0">
          {message.severity === 'error' && <Message severity={message.severity} message={message.errorMessage} />}
          {message.severity === 'success' ? (
            <Message severity={message.severity} message={message.errorMessage} />
          ) : (
            <form onSubmit={handleNewspaperSignup} className="flex w-full flex-col items-center space-y-5 ">
              <Input
                className="mb-15 mt-8 h-40 w-full rounded-md border border-gray-300 px-15 text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                onChange={handleInputChange}
                name="email"
                value={emailData}
                placeholder="Enter your email address"
                isFooter={true}
                labelPosition="inline"
                isValid={emailField.validation && emailField?.validation(emailData)}
              />
              <button className="h-40 w-full rounded-md bg-[#00d195] font-semibold text-white transition-all duration-300 hover:bg-[#00b881]">
                {signUp?.cta?.name}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default GolfHolidayDirect;
